import { supabase } from '../lib/supabase';

// Helper to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return null;
  return {
    id: obj.id,
    title: obj.title,
    description: obj.description,
    backgroundImageUrl: obj.background_image_url,
    actionButtonText: obj.action_button_text,
    actionType: obj.action_type,
    targetLink: obj.target_link,
    isActive: obj.is_active,
    displayOrder: obj.display_order,
    badgeText: obj.badge_text,
    badgeColor: obj.badge_color,
    duration: obj.duration || 5000,
    // New fields
    location: obj.location || 'homepage_carousel',
    cpmRate: parseFloat(obj.cpm_rate || 0),
    cpcRate: parseFloat(obj.cpc_rate || 0),
    heightStyle: obj.height_style || 'row-height',
    // Dynamic Overlay Shade, Image Fit, Image Position, Text Position, and Ad Width
    overlayShade: (() => {
      if (obj.height_style && obj.height_style.includes('shade=')) {
        const match = obj.height_style.match(/(?:^|::)shade=([^:]+)/);
        if (match && match[1]) return match[1];
      }
      return obj.overlay_shade || 'none';
    })(),
    imageFit: (() => {
      if (obj.height_style && obj.height_style.includes('fit=')) {
        const match = obj.height_style.match(/(?:^|::)fit=([^:]+)/);
        if (match && match[1]) return match[1];
      }
      return obj.image_fit || 'contain';
    })(),
    imagePosition: (() => {
      if (obj.height_style && obj.height_style.includes('img_pos=')) {
        const match = obj.height_style.match(/(?:^|::)img_pos=([^:]+)/);
        if (match && match[1]) return match[1];
      }
      return obj.image_position || 'left';
    })(),
    textPosition: (() => {
      if (obj.height_style && obj.height_style.includes('pos=')) {
        const match = obj.height_style.match(/(?:^|::)pos=([^:]+)/);
        if (match && match[1]) return match[1];
      }
      return obj.text_position || 'center';
    })(),
    adWidth: (() => {
      if (obj.height_style && obj.height_style.includes('width=')) {
        const match = obj.height_style.match(/(?:^|::)width=([^:]+)/);
        if (match && match[1]) return match[1];
      }
      return obj.ad_width || 'auto';
    })(),
    showTitle: (() => {
      if (obj.height_style && obj.height_style.includes('show_title=')) {
        const match = obj.height_style.match(/(?:^|::)show_title=([^:]+)/);
        if (match && match[1]) return match[1] === 'true';
      }
      if (obj.show_title !== undefined) return obj.show_title;
      return true;
    })(),
    campaignBudget: parseFloat(obj.campaign_budget || 0),
    approvalStatus: obj.approval_status || 'approved',
    rejectionReason: obj.rejection_reason || null,
    sellerId: obj.seller_id,
    startDate: obj.start_date,
    endDate: obj.end_date,
    // Add processed products if available (from join)
    products: obj.advertisement_products?.map(ap => ({
      ...ap.products, // The joined product data
      // Add standard fields that might be needed
    })) || []
  };
};

export const advertisementService = {
  /**
   * Record an impression (view) for a banner
   */
  async recordImpression(bannerId, userId = null) {
    try {
      await supabase.from('advertisement_analytics').insert({
        banner_id: bannerId,
        event_type: 'view',
        user_id: userId
      });
    } catch (error) {
      console.error('Failed to record impression:', error);
    }
  },

  /**
   * Record a click for a banner
   */
  async recordClick(bannerId, userId = null) {
    try {
      await supabase.from('advertisement_analytics').insert({
        banner_id: bannerId,
        event_type: 'click',
        user_id: userId
      });
    } catch (error) {
      console.error('Failed to record click:', error);
    }
  },

  /**
   * Get all active banners ordered by display_order
   */
  async getBanners() {
    return this.getActiveBanners();
  },

  async getActiveBanners() {
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('advertisement_banners')
        .select(`
          *,
          advertisement_products (
            product_id,
            display_order,
            products (
              id,
              name,
              price,
              thumbnail_path,
              average_rating
            )
          )
        `)
        .eq('is_active', true)
        .or(`start_date.is.null,start_date.lte.${now}`)
        .or(`end_date.is.null,end_date.gte.${now}`)
        .order('display_order', { ascending: true });

      if (error) throw error;

      return data.map(toCamelCase);
    } catch (error) {
      console.error('advertisementService: Error fetching banners:', error);
      return [];
    }
  },

  /**
   * Get a specific banner by ID
   */
  async getById(id) {
    try {
      const { data, error } = await supabase
        .from('advertisement_banners')
        .select(`
          *,
          advertisement_products (
            product_id,
            display_order,
            products (
              id,
              name,
              price,
              thumbnail_path,
              average_rating
            )
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      return toCamelCase(data);
    } catch (error) {
      console.error(`advertisementService: Error fetching banner ${id}:`, error);
      return null;
    }
  },

  /**
   * Get all banners assigned to a specific seller
   */
  async getBannersBySellerId(sellerId) {
    if (!sellerId) return [];
    try {
      const { data, error } = await supabase
        .from('advertisement_banners')
        .select(`
          *,
          advertisement_products (
            product_id,
            display_order,
            products (
              id,
              name,
              price,
              thumbnail_path
            )
          )
        `)
        .eq('seller_id', sellerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(toCamelCase);
    } catch (error) {
      console.error('advertisementService: Error fetching seller banners:', error);
      return [];
    }
  },

  /**
   * Get analytics counts (views/impressions and clicks) for a banner
   */
  async getAnalytics(bannerId, startDate = null, endDate = null) {
    if (!bannerId) return { views: 0, clicks: 0 };
    try {
      let viewsQuery = supabase
        .from('advertisement_analytics')
        .select('id', { count: 'exact', head: true })
        .eq('banner_id', bannerId)
        .eq('event_type', 'view');

      let clicksQuery = supabase
        .from('advertisement_analytics')
        .select('id', { count: 'exact', head: true })
        .eq('banner_id', bannerId)
        .eq('event_type', 'click');

      if (startDate) {
        viewsQuery = viewsQuery.gte('created_at', startDate);
        clicksQuery = clicksQuery.gte('created_at', startDate);
      }
      if (endDate) {
        viewsQuery = viewsQuery.lte('created_at', endDate);
        clicksQuery = clicksQuery.lte('created_at', endDate);
      }

      const { count: views, error: viewsError } = await viewsQuery;
      const { count: clicks, error: clicksError } = await clicksQuery;

      if (viewsError || clicksError) {
        console.error('advertisementService: Error fetching analytics counts:', viewsError || clicksError);
        return { views: 0, clicks: 0 };
      }

      return { views: views || 0, clicks: clicks || 0 };
    } catch (err) {
      console.error('advertisementService: Exception in getAnalytics:', err);
      return { views: 0, clicks: 0 };
    }
  },

  /**
   * Submit an ad banner for administrator approval with requested budget
   */
  async submitAdForApproval(adData) {
    const {
      sellerId,
      title,
      description,
      backgroundImageUrl,
      location = 'catalog_inline',
      targetLink = null,
      actionButtonText = 'Explore Now',
      campaignBudget = 0.00,
      adWidth = 'half',
      startDate = null,
      endDate = null
    } = adData;

    try {
      const { data, error } = await supabase.rpc('submit_seller_banner_ad', {
        p_seller_id: sellerId,
        p_title: title,
        p_description: description,
        p_background_image_url: backgroundImageUrl,
        p_location: location,
        p_target_link: targetLink,
        p_action_button_text: actionButtonText,
        p_campaign_budget: parseFloat(campaignBudget) || 0,
        p_ad_width: adWidth,
        p_start_date: startDate,
        p_end_date: endDate
      });

      if (!error && data) {
        if (data.success === false) {
          throw new Error(data.error || 'Failed to submit ad for approval.');
        }
        return data;
      }

      if (error && !error.message?.includes('function submit_seller_banner_ad') && !error.message?.includes('does not exist')) {
        throw error;
      }
    } catch (rpcErr) {
      if (rpcErr.message && !rpcErr.message.includes('function submit_seller_banner_ad') && !rpcErr.message.includes('does not exist')) {
        throw rpcErr;
      }
    }

    // Direct fallback insertion
    const { data: inserted, error: insertErr } = await supabase
      .from('advertisement_banners')
      .insert({
        seller_id: sellerId,
        title,
        description,
        background_image_url: backgroundImageUrl,
        location,
        target_link: targetLink,
        action_button_text: actionButtonText,
        action_type: 'link',
        campaign_budget: parseFloat(campaignBudget) || 0,
        height_style: `320px::width=${adWidth}`,
        cpm_rate: 2.50,
        cpc_rate: 0.25,
        is_active: false,
        approval_status: 'pending_approval',
        start_date: startDate || new Date().toISOString(),
        end_date: endDate
      })
      .select()
      .single();

    if (insertErr) {
      console.error('Error inserting banner ad directly:', insertErr);
      throw insertErr;
    }

    return { success: true, banner_id: inserted?.id };
  }
};
